import React from 'react';
import Table from 'react-bootstrap/Table';

const SpreadsMiniTable = ({
  spreadsData,
  getBookLogoUrl,
  getTeamLogoUrl,
  getCellStyle,
  getTooltipText,
  onOddsClick,
  game,
  periodsToShow = null
}) => {
  const safeGame = { away: 'TBD', home: 'TBD', gameIDmlb: 'loading', ...game };

  if (!spreadsData || spreadsData.length === 0) {
    return null;
  }

  // Group by period
  const groupedByPeriod = spreadsData.reduce((acc, player) => {
    const period = player.period || 'Game';
    if (!acc[period]) {
      acc[period] = [];
    }
    acc[period].push(player);
    return acc;
  }, {});

  // Define period order
  const periodOrder = ['Game', 'Q1', 'Q2', 'Q3', 'Q4', '1st Half', '2nd Half', '1st', '2nd', '3rd'];

  // Filter periods based on periodsToShow prop
  let periodsToDisplay = Object.keys(groupedByPeriod);
  if (periodsToShow && Array.isArray(periodsToShow)) {
    periodsToDisplay = periodsToDisplay.filter(period => periodsToShow.includes(period));
  }

  const sortedPeriods = periodsToDisplay.sort((a, b) => {
    const indexA = periodOrder.indexOf(a);
    const indexB = periodOrder.indexOf(b);
    return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
  });

  // Function to find best odds
  const findBestOdds = (player) => {
    const books = ['fanduel', 'draftkings', 'betmgm', 'caesars', 'bet365', 'pointsbet', 'betrivers', 'hardrockbet', 'bovada', 'fanatics', 'espnbet'];
    let bestOdds = null;
    let bestBook = null;
    let bookCount = 0;

    books.forEach(book => {
      const odds = player[`spreads_${book}`];
      if (odds !== null && odds !== undefined) {
        bookCount++;
        if (bestOdds === null || odds > bestOdds) {
          bestOdds = odds;
          bestBook = book;
        }
      }
    });

    const bestBookPrice = player.spreads_bestBookPrice;
    const bestBookDiff = player.spreads_bestBookDiff;

    return { bestOdds, bestBook, bestBookPrice, bestBookDiff, bookCount };
  };

  return (
    <div className="table-container">
      <Table bordered size="sm" className="text-center">
        <tbody>
          {sortedPeriods.map((period) => {
            // Group by team for this period
            const periodData = groupedByPeriod[period];
            const awayData = periodData.filter(p => p.team === 'away');
            const homeData = periodData.filter(p => p.team === 'home');

            // Find most favorable spread for each team (highest point value = best for bettor)
            const bestAwaySpread = awayData.reduce((max, curr) =>
              !max || curr.point > max.point ? curr : max
            , null);

            const bestHomeSpread = homeData.reduce((max, curr) =>
              !max || curr.point > max.point ? curr : max
            , null);

            return (
              <React.Fragment key={period}>
                {/* Period section header */}
                <tr>
                  <td colSpan={4} style={{
                    backgroundColor: 'transparent',
                    color: '#ffc107',
                    fontWeight: 'bold',
                    textAlign: 'center',
                    padding: '8px',
                    borderTop: 'none',
                    borderBottom: 'none'
                  }}>
                    {period === 'Game' ? 'Spreads' : `Spreads ${period}`}
                  </td>
                </tr>
                {/* Away team row */}
                {bestAwaySpread && (() => {
                  const { bestOdds, bestBook, bestBookPrice, bestBookDiff, bookCount } = findBestOdds(bestAwaySpread);
                  return (
                    <tr key={`${period}-away`}>
                      <td style={{ fontWeight: 'bold' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                          <img
                            src={safeGame.awayLogo}
                            alt={safeGame.away}
                            style={{ width: '16px', height: '16px' }}
                            onError={(e) => {
                              e.target.style.visibility = 'hidden';
                              e.target.style.width = '0px';
                              e.target.style.height = '0px';
                              e.target.style.margin = '0px';
                            }}
                          />
                          <span>{safeGame.away}</span>
                        </div>
                      </td>
                      <td>
                        {bestBook && (
                          <img
                            src={getBookLogoUrl(bestBook)}
                            alt={bestBook}
                            style={{ height: '16px', width: 'auto' }}
                          />
                        )}
                      </td>
                      <td style={{ fontWeight: 'bold' }}>
                        {bestAwaySpread.point > 0 ? `+${bestAwaySpread.point}` : bestAwaySpread.point}
                      </td>
                      <td
                        style={{
                          ...getCellStyle(bestOdds, bestBookPrice, bestBookDiff, bookCount),
                          cursor: bestOdds ? 'pointer' : 'default',
                          fontWeight: 'bold'
                        }}
                        title={getTooltipText(bestOdds, bestBookPrice, bestBookDiff, bookCount)}
                        onClick={() => {
                          if (bestOdds && bestBook && onOddsClick) {
                            onOddsClick(safeGame.gameIDmlb, bestAwaySpread.playerId, bestAwaySpread.team, 'spreads', bestBook, bestOdds, safeGame.away, safeGame.home);
                          }
                        }}
                      >
                        {bestOdds > 0 ? `+${bestOdds}` : bestOdds}
                      </td>
                    </tr>
                  );
                })()}
                {/* Home team row */}
                {bestHomeSpread && (() => {
                  const { bestOdds, bestBook, bestBookPrice, bestBookDiff, bookCount } = findBestOdds(bestHomeSpread);
                  return (
                    <tr key={`${period}-home`}>
                      <td style={{ fontWeight: 'bold' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                          <img
                            src={safeGame.homeLogo}
                            alt={safeGame.home}
                            style={{ width: '16px', height: '16px' }}
                            onError={(e) => {
                              e.target.style.visibility = 'hidden';
                              e.target.style.width = '0px';
                              e.target.style.height = '0px';
                              e.target.style.margin = '0px';
                            }}
                          />
                          <span>{safeGame.home}</span>
                        </div>
                      </td>
                      <td>
                        {bestBook && (
                          <img
                            src={getBookLogoUrl(bestBook)}
                            alt={bestBook}
                            style={{ height: '16px', width: 'auto' }}
                          />
                        )}
                      </td>
                      <td style={{ fontWeight: 'bold' }}>
                        {bestHomeSpread.point > 0 ? `+${bestHomeSpread.point}` : bestHomeSpread.point}
                      </td>
                      <td
                        style={{
                          ...getCellStyle(bestOdds, bestBookPrice, bestBookDiff, bookCount),
                          cursor: bestOdds ? 'pointer' : 'default',
                          fontWeight: 'bold'
                        }}
                        title={getTooltipText(bestOdds, bestBookPrice, bestBookDiff, bookCount)}
                        onClick={() => {
                          if (bestOdds && bestBook && onOddsClick) {
                            onOddsClick(safeGame.gameIDmlb, bestHomeSpread.playerId, bestHomeSpread.team, 'spreads', bestBook, bestOdds, safeGame.away, safeGame.home);
                          }
                        }}
                      >
                        {bestOdds > 0 ? `+${bestOdds}` : bestOdds}
                      </td>
                    </tr>
                  );
                })()}
              </React.Fragment>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
};

export default SpreadsMiniTable;
